import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/LanguageContext";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";
// Relative: resolvable under Jest without a shim.
import CodeField from "../saas/signup/CodeField";
import { PLATFORM_OFFERS as IDS } from "../constants/testIds/platformOffers";
import { redeemCode } from "./api";
import { platformOfferErrorMessage } from "./errors";
import { tenantPricing } from "./useSaasCodePreview";

// The toast after a successful redeem, by what the server did (contract §5.4): a promo
// during the trial is `scheduled` (attached when the trial ends, §5.0), otherwise applied.
const REDEEMED_KEYS = {
  "promo:applied": "bill.offers.redeemedPromo",
  "promo:scheduled": "bill.offers.redeemedPromoScheduled",
  "giftcard:applied": "bill.offers.redeemedGiftcard",
};

/**
 * "Redeem a code" on the Billing page (contract §5.4): a platform promo code (attached to
 * the current subscription) or a gift card (its whole balance moved to the practice's
 * Stripe credit). Preview first, against the CURRENT plan, so the practitioner sees what
 * the code does before spending it; Redeem is the call that spends it.
 *
 * @param {object} tenant       `billing/summary.tenant`
 * @param {boolean} trialing    `billing/summary.trial.state === "trialing"`
 * @param {(redemption) => void} onRedeemed  after a successful redeem (reload the list)
 * @param {function} t          translator bound to "admin"
 */
export default function BillingRedeemCard({ tenant, trialing, onRedeemed, t }) {
  const tc = useT("common");
  const [preview, setPreview] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const redeem = async () => {
    setRedeeming(true);
    try {
      const redemption = await redeemCode(preview.code);
      toast.success(t(REDEEMED_KEYS[`${redemption.kind}:${redemption.status}`] || "bill.offers.redeemed"));
      setPreview(null);
      onRedeemed(redemption);
    } catch (e) {
      toast.error(platformOfferErrorMessage(e, tc, t("bill.error")));
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-eh-border-soft p-6" data-testid={IDS.billingRedeemCard}>
      <h2 className="font-display text-xl text-eh-ink flex items-center gap-2 mb-1">
        <Gift className="w-5 h-5 text-sol-gold" /> {t("bill.offers.redeemTitle")}
      </h2>
      <p className="text-sm text-eh-muted mb-4">{t("bill.offers.redeemSub")}</p>
      <CodeField
        scope="billing-redeem"
        value={preview}
        params={tenantPricing(tenant)}
        disabled={redeeming}
        onApplied={setPreview}
        onRemoved={() => setPreview(null)}
      />
      {preview && (
        <div className="mt-4 flex items-center justify-between gap-4">
          <p className="text-xs text-eh-muted">
            {preview.kind === "promo"
              ? t(trialing ? "bill.offers.promoAfterTrialNote" : "bill.offers.promoReplacesNote")
              : t("bill.offers.giftcardNote")}
          </p>
          <Button
            onClick={redeem}
            disabled={redeeming}
            data-testid={IDS.billingRedeemSubmit}
            className="bg-sol-gold text-sol-midnight hover:bg-sol-gold/90 flex-shrink-0"
          >
            {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : t("bill.offers.redeemSubmit")}
          </Button>
        </div>
      )}
    </div>
  );
}
